import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { DialogResult } from 'src/app/models/dialog-result';
import { IMasterSuiteTouch, IMasterSuiteTouchAsset } from 'src/app/models/mastersuites/mastersuite';
import {
  AssetMappingAction,
  IAssetMapping,
  IProductRef,
  ITouchMapping,
  ITransferDestination,
  ITransferPrepareResult,
  ITransferSource,
  TouchMappingAction,
} from 'src/app/models/products/actions/settings/transferProductSettings';
import { ProductsService } from 'src/app/services/products.service';
import { ConfirmModalComponent } from '../../../../shared/confirm-modal/confirm-modal.component';
import { WizardEnhancementVariantRef } from '../select-master-suites-step/select-master-suites-step.component';

export interface MapAssetsStepData {
  source: ITransferSource;
  destination: ITransferDestination;
  sourceEnhancementVariant: WizardEnhancementVariantRef;
  destinationEnhancementVariant: WizardEnhancementVariantRef;
  products: IProductRef[];
  touchMappings: ITouchMapping[];
  prepareResult: ITransferPrepareResult;
}

export interface MapAssetsStepResult {
  confirmed: boolean;
}

@Component({
  selector: 'app-map-assets-step',
  templateUrl: './map-assets-step.component.html',
  styleUrls: ['./map-assets-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class MapAssetsStepComponent {
  private readonly destroyRef = inject(DestroyRef);

  assetMapsBySourceTouchId: { [sourceTouchId: string]: IAssetMapping[] } = {};
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MapAssetsStepData,
    private readonly dialogRef: MatDialogRef<MapAssetsStepComponent>,
    private readonly productsService: ProductsService,
    private readonly dialog: MatDialog,
  ) {}

  get mapMappings(): ITouchMapping[] {
    return this.data.touchMappings.filter(
      (mapping) => mapping.action === TouchMappingAction.Map && !!mapping.destinationTouchId,
    );
  }

  get dropMappings(): ITouchMapping[] {
    return this.data.touchMappings
      .filter((mapping) => mapping.action === TouchMappingAction.Drop)
      .sort((a, b) => this.mappingSortIndex(a) - this.mappingSortIndex(b));
  }

  private mappingSortIndex(mapping: ITouchMapping): number {
    return this.sourceTouchFor(mapping.sourceTouchId)?.index ?? 0;
  }

  get newDestinationTouches(): IMasterSuiteTouch[] {
    const claimedIds = new Set(
      this.data.touchMappings
        .filter((mapping) => mapping.action === TouchMappingAction.Map && !!mapping.destinationTouchId)
        .map((mapping) => mapping.destinationTouchId!),
    );
    return (this.data.prepareResult.masterSuiteDestination.masterSuiteTouches ?? [])
      .filter((touch) => !claimedIds.has(touch.id!))
      .sort((a, b) => a.index - b.index);
  }

  get sourceMasterSuiteName(): string {
    return this.data.prepareResult.masterSuiteSource.name ?? '';
  }

  get destinationMasterSuiteName(): string {
    return this.data.prepareResult.masterSuiteDestination.name ?? '';
  }

  get destinationEnhancementVariantName(): string {
    return this.data.destinationEnhancementVariant.name;
  }

  get sourceEnhancementVariantName(): string {
    return this.data.sourceEnhancementVariant.name;
  }

  sourceTouchFor(sourceTouchId: string): IMasterSuiteTouch | undefined {
    return this.data.prepareResult.masterSuiteSource.masterSuiteTouches?.find((touch) => touch.id === sourceTouchId);
  }

  destinationTouchFor(destinationTouchId: string | undefined): IMasterSuiteTouch | undefined {
    if (!destinationTouchId) return undefined;
    return this.data.prepareResult.masterSuiteDestination.masterSuiteTouches?.find((touch) => touch.id === destinationTouchId);
  }

  touchLabel(touch: IMasterSuiteTouch): string {
    return `(touch ${touch.index / 1000 + 1}) ${touch.name} (package) ${touch.packageName}`;
  }

  assetLabel(asset: IMasterSuiteTouchAsset): string {
    return `${asset.packageAssetType} · ${asset.packageAssetRobotOutDegrees}° · table ${asset.packageAssetTablePosition}`;
  }

  droppedTouchAssets(sourceTouchId: string): IMasterSuiteTouchAsset[] {
    const touch = this.sourceTouchFor(sourceTouchId);
    return touch?.masterSuiteTouchAssets ?? [];
  }

  affectedProductCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).length;
  }

  shootAssetCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).reduce((total, product) => total + (product.shootAssetCount ?? 0), 0);
  }

  postAssetCount(sourceTouchId: string): number {
    return this.affectedProductsFor(sourceTouchId).reduce((total, product) => total + (product.postAssetCount ?? 0), 0);
  }

  private affectedProductsFor(sourceTouchId: string) {
    return this.data.prepareResult.affectedProductsBySourceTouchId?.[sourceTouchId] ?? [];
  }

  onAssetMapChanged(sourceTouchId: string, assetMap: IAssetMapping[]): void {
    this.assetMapsBySourceTouchId = { ...this.assetMapsBySourceTouchId, [sourceTouchId]: assetMap };
  }

  buildTouchMappingsWithAssetMaps(): ITouchMapping[] {
    return this.data.touchMappings.map((touchMapping) => {
      const assetMap = this.assetMapsBySourceTouchId[touchMapping.sourceTouchId];
      return assetMap ? { ...touchMapping, assetMap } : touchMapping;
    });
  }

  confirm(): void {
    if (this.submitting) return;
    const finalTouchMappings = this.buildTouchMappingsWithAssetMaps();
    const deletionSummary = this.buildDeletionSummary(finalTouchMappings);
    if (deletionSummary === null) {
      this.executeTransfer(finalTouchMappings);
      return;
    }
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          header: 'Confirm deletions',
          content: deletionSummary,
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed === true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.executeTransfer(finalTouchMappings));
  }

  private executeTransfer(touchMappings: ITouchMapping[]): void {
    this.submitting = true;
    this.productsService
      .executeProductSettingsTransfer({
        source: this.data.source,
        destination: this.data.destination,
        products: this.data.products,
        touchMappings,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close({ confirmed: true } as MapAssetsStepResult),
        error: () => {
          this.submitting = false;
        },
      });
  }

  private buildDeletionSummary(touchMappings: ITouchMapping[]): string | null {
    const droppedTouchCount = touchMappings.filter((mapping) => mapping.action === TouchMappingAction.Drop).length;
    const discardedAssetCount = touchMappings.reduce(
      (total, mapping) => total + (mapping.assetMap?.filter((asset) => asset.action === AssetMappingAction.Discard).length ?? 0),
      0,
    );
    if (droppedTouchCount === 0 && discardedAssetCount === 0) return null;
    const lines: string[] = [];
    if (droppedTouchCount > 0) lines.push(`${droppedTouchCount} touch(es) will be deleted along with their shoots and post assets.`);
    if (discardedAssetCount > 0) lines.push(`${discardedAssetCount} asset(s) placed in Discard will be deleted.`);
    lines.push('This cannot be undone.');
    return lines.join(' ');
  }

  cancel(): void {
    this.dialogRef.close(DialogResult.Cancel);
  }
}
